import React, { useEffect, useMemo, useState } from "react";
import "./AcademyHero.css";
import { FiArrowRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import heroImage from "../../../../assets/images/academy/academyhero.png";
import { scrollToElementById } from "../../../../utils/scrollToId";
import { getPageContent } from "../../../../api/content";
import { resolveMediaUrl } from "../../../../utils/media";

const FALLBACK_HERO = {
  tag: "ACADEMY",
  title: "Learn Today.\nLead Tomorrow",
  description:
    "Access certified training programs designed to build skills, advance careers, and support construction professionals at every level. Your future in construction starts here.",
  ctaText: "Discover courses",
  backgroundImage: heroImage,
};

const AcademyHero = () => {
  const { i18n } = useTranslation();
  const [heroContent, setHeroContent] = useState(FALLBACK_HERO);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const payload = await getPageContent({
          page: "academy",
          language: String(i18n.language || "en").toLowerCase(),
        });
        if (!isMounted) return;

        // Preferred API shape:
        // { hero: { data: { tag, title, description, ctaText, backgroundImage } } }
        const heroDataObject =
          payload?.hero?.data && typeof payload.hero.data === "object"
            ? payload.hero.data
            : null;

        // Backward-compatible fallback for flat row arrays.
        const heroRows = (Array.isArray(payload) ? payload : [])
          .filter(
            (row) =>
              String(row?.section || "").toLowerCase() === "hero" &&
              (row?.isActive ?? true)
          )
          .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0));

        const heroRowsByKey = heroRows.reduce((acc, row) => {
          const key = String(row?.key || "").trim();
          if (!key) return acc;
          acc[key] = row?.value ?? "";
          return acc;
        }, {});

        const source = heroDataObject || heroRowsByKey;

        const apiBackground = resolveMediaUrl(source.backgroundImage);

        setHeroContent({
          tag: String(source.tag || FALLBACK_HERO.tag),
          title: String(source.title || FALLBACK_HERO.title),
          description: String(source.description || FALLBACK_HERO.description),
          ctaText: String(source.ctaText || FALLBACK_HERO.ctaText),
          backgroundImage: apiBackground || FALLBACK_HERO.backgroundImage,
        });
      } catch {
        if (isMounted) {
          setHeroContent(FALLBACK_HERO);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const titleLines = useMemo(
    () =>
      String(heroContent?.title || FALLBACK_HERO.title)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    [heroContent?.title]
  );

  const handleDiscoverCourses = () => {
    scrollToElementById("academy-courses");
  };

  return (
    <section className="academyHero" aria-label="Academy hero">
      
      {/* BG IMAGE */}
      <div className="academyHero-bg">
        <img
          src={heroContent.backgroundImage}
          alt=""
          className="academyHero-bgImg"
          draggable={false}
        />
      </div>

      {/* OVERLAY */}
      <div className="academyHero-overlay" aria-hidden="true" />

      {/* CONTENT */}
      <div className="academyHero-inner">
        <span className="academyHero-tag">{heroContent.tag}</span>

        <h1 className="academyHero-title">
          {titleLines[0] || FALLBACK_HERO.title}
          {titleLines[1] ? (
            <>
              <br />
              <span className="greytext">{titleLines[1]}</span>
            </>
          ) : null}
        </h1>

        <p className="academyHero-text">{heroContent.description}</p>

        <div className="academyHero-ctaRow">
          <button type="button" className="academyHero-cta" onClick={handleDiscoverCourses}>
            {heroContent.ctaText}
            <FiArrowRight className="academyHero-ctaIcon" />
          </button>
        </div>
      </div>

    </section>
  );
};

export default AcademyHero;